/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';
import isObjectLike from 'lodash/isObjectLike';

import config from '../../config';
import { errorWithLevelWarning } from '../error/error-with-level';
import { fetchTextThrowNotOk, fetchWithTimeout } from '../fetch/fetch';
import CommunicatorHeartbeat from './heartbeat/communicator-heartbeat';
import CommunicatorManifest from './manifest/communicator-manifest';
import CommunicatorQueue, { QueueIdEnum } from './queue/communicator-queue';
import CommunicatorReport from './report/communicator-report';
import CommunicatorUpdate from './update/communicator-update';

/**
 * @import UpdatePlatform from '../../platform/update-platform'
 * @import { TimestampString } from '../communicator'
 * @import { ErrorEventListener } from '../event-listeners'
 * @import FormatEnum from '../format-enum'
 * @import { HeartbeatFetched } from '../heartbeat'
 * @import { JSONValue } from '../json'
 * @import { ReportInfo } from '../report'
 * @import { ServerDeviceId } from '../session/session-attrs'
 * @import { UpdateResult } from './update/update-result'
 */

/**
 * @typedef {import('../event-bus').EventBus<EventTypes>} EventBus
 */

// TODO #94 Přidat unit testy pro nové funkcionality spojené s offline módem.
// TODO #626: Všechny metody s deviceId a serverUrl parametry zároveň refaktorovat na ServerDeviceId.
/**
 * Komunikátor pro Ki-Wi Server
 * @implements {EventBus}
 */
export default class Communicator {
  /**
   * Vytvoří komunikátor zařízení se serverem.
   */
  constructor() {
    // Stavové komunikátory
    this.communicatorHeartbeat = new CommunicatorHeartbeat();
    // Příznak, jestli je synchronizace zapnuta.
    this.connected = false;
    /** @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();
    this.fetching = false;
    // Výchozí doba čekání před novým pokusem o synchronizaci.
    this.syncDelayBase = config.device.communicator.syncDelayBase;
    // Identifikátor intervalu ve kterém probíhá synchronizace.
    /** @type {ReturnType<typeof setInterval> | undefined} */
    this.syncInterval = undefined;
    // Navázání naslouchání na instanci.
    this.onError = this.onError.bind(this);
  }

  /**
   * Spustí kontinuální synchronizaci obsahu zařízení.
   *
   * Vracet objekty ve store, které byly během operace načteny.
   *
   * Vracet plain objekty. Player aplikace sama rozhoduje, jaké datový sklad bude používat.
   * Zároveň by neměla mít přístup k interním datům device.
   *
   * Vracet mapu polí modelů. Klíčem v mapě je název datového zdroje.
   * Např. `result.medium[0]` bude mít obsah média prvního načteného média.
   * Možná ještě zvážit mapu map modelů, kde by druhým klíčem bylo id modelu.
   * Např. `result.medium.foo` bude mít obsah média s identifikátorem `foo`.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @returns {Promise<void>} Vyřeší se pod dokončení první synchronizace.
   */
  connect(serverUrl, deviceId) {
    return new Promise((resolve) => {
      if (this.connected) {
        throw new Error('Already connected.');
      }
      // Uzamče další spuštění synchronizace.
      this.connected = true;
      // Naplánuje interval, ve kterém se bude spuštět synchronizace.
      this.syncInterval = setInterval(
        () => this.onSyncInterval(serverUrl, deviceId),
        this.syncDelayBase
      );
      // Spustí první synchronizaci ihned.
      return resolve(
        this.fetchHeartbeat(serverUrl, deviceId).catch(this.onError)
      );
    });
  }

  /**
   * Uvolní všechny zdroje pro garbage collection.
   */
  destroy() {
    // Zruší interval synchronizace.
    clearInterval(this.syncInterval);
    this.syncInterval = undefined;
  }

  /**
   * Získává surový JSON objekt z fronty příkazů zařízení ({@link QueueIdEnum.COMMAND}).
   * @param {ServerDeviceId} serverDeviceId
   * @returns {Promise<JSONValue | undefined>} JSON nebo `undefined`, pokud je fronta prázdná.
   * @throws {Warning}
   */
  async fetchCommandJson(serverDeviceId) {
    return await CommunicatorQueue.fetch(serverDeviceId, QueueIdEnum.COMMAND);
  }

  /**
   * Doručí obsah zařízení.
   * @param {TimestampString | null} contentTimestamp Časový otisk aktuálně přehrávaného obsahu.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {TimestampString | null} heartbeatTimestamp Časový otisk okamžiku synchronizace.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @returns {Promise<string | null>} Vrátí contentSettings jako text, pokud je dostupný.
   */
  fetchContentSettings(
    contentTimestamp,
    deviceId,
    heartbeatTimestamp,
    serverUrl
  ) {
    if (
      this.fetching ||
      // Při první heartbeat ze serveru je contentTimestamp null.
      (isNil(contentTimestamp) && isNil(heartbeatTimestamp)) ||
      contentTimestamp === heartbeatTimestamp
    ) {
      // Nové settings zatím není uloženo, ale obsah se nezměnil, tak budu hrát to co mám.
      return Promise.resolve(null);
    }
    this.fetching = true;
    return new Promise((resolve) =>
      resolve(
        // Stažení JSON obsahu zařízení.
        fetchTextThrowNotOk(`${serverUrl}/device/generator/${deviceId}/3`)
          .catch((error) => {
            // Snížení priority logování pro chyby síťového spojení.
            throw errorWithLevelWarning(error);
          })
          .finally(() => {
            this.fetching = false;
          })
      )
    );
  }

  /**
   * Odešle heartbeat a získá heartbeat data pro zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @private
   * @returns {Promise<void>} Vyřeší se po úspěšném získání heartbeat dat a odeslání události heartbeatFetched s hodnotou typu HeartbeatFetched.
   */
  fetchHeartbeat(serverUrl, deviceId) {
    return (
      // Stáhne push data.
      this.communicatorHeartbeat
        .fetch(deviceId, serverUrl)
        // TODO #44 Zpracování reakce na heartbeat a posloupnost content / queue / version.
        // TODO #44 Co když heartbeat přijde dříve, než jsou předchozí informace zpracovány?
        .then((heartbeatFetched) => {
          // heartbeatFetched může být null v případě, že již probíhá fetch a přijde současně jiný požadavek.
          // TODO Tyto stavy by se neměly tiše spolknout, ale zalogovat jako warning nebo zaevidovat jako metrika.
          if (isNil(heartbeatFetched) || !isObjectLike(heartbeatFetched)) {
            // Pokud se nepodařilo heartbeat načíst, tak se nic nedělá.
            return;
          }
          // Pokud se podařilo heartbeat načíst, tak se vyvolá událost, která o něm informuje.
          this.emitter.emit('heartbeatFetched', heartbeatFetched);
        })
    );
  }

  /**
   * Reakce na chybu.
   * @param {Error} error Chybový objekt.
   * @private
   * @returns {void} Nic.
   */
  onError(error) {
    this.emitter.emit('error', error);
  }

  /** @type {EventBus['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {EventBus['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce uplynutí intervalu ve kterém se synchronizuje obsah.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @private
   */
  onSyncInterval(serverUrl, deviceId) {
    // Spustí jednorázovou synchronizaci.
    this.fetchHeartbeat(serverUrl, deviceId).catch(this.onError);
  }

  /**
   * Odešle informace o zařízení na server.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {ReportInfo} reportInfo Informace o zařízení.
   * @returns {Promise<void>} Vyřeší se po úspěšném dokončení.
   */
  report(serverUrl, deviceId, reportInfo) {
    return CommunicatorReport.fetch(reportInfo, deviceId, serverUrl);
  }

  /**
   * Doručí všechny potřebné informace pro aktualizaci zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {FormatEnum} formatEnum Formát, ve kterém provést aktualizaci.
   * @param {UpdatePlatform} updatePlatform Platforma, ve které provést aktualizaci.
   * @returns {Promise<UpdateResult | null>} Informace pro aktualizaci zařízení.
   */
  update(serverUrl, deviceId, formatEnum, updatePlatform) {
    return new Promise((resolve) => {
      // Kontrola vstupů.
      if (!formatEnum) {
        throw new TypeError('Invalid formatEnum.');
      }
      if (!updatePlatform) {
        throw new TypeError('Invalid updatePlatform.');
      }
      return resolve(
        // Zjistí se informace o verzi a dostupných manifestech.
        CommunicatorUpdate.fetch(deviceId, serverUrl)
          // TODO #44 Na co v tomto API položka version? Ta už byla kontrolována v heartbeat.
          .then(({ urlArray, version }) => {
            // Pokud není definována verze, nic neaktualizujeme.
            if (!version) {
              return Promise.resolve(null);
            }
            // Kontrola dostupných manifestů.
            if (!urlArray.length) {
              throw new Error('No version manifest available.');
            }
            // Stažení manifestů.
            return (
              // Jednotlivé manifesty se začnou sériově načítat, dokud nějaký nenalezne vyhovující build.
              urlArray
                .reduce(
                  (acc, url) =>
                    acc.catch(() => {
                      const communicatorManifest = new CommunicatorManifest(
                        url
                      );
                      return (
                        // Zjistí se informace z manifestu.
                        communicatorManifest
                          .fetch()
                          .then((manifestData) => {
                            // Profiltruje se seznam dostupných buildů, aby odpovídal požadavkům ve vstupu.
                            const buildArray = manifestData.buildArray.filter(
                              (build) =>
                                build.formatEnum === formatEnum &&
                                build.updatePlatform === updatePlatform
                            );
                            // Pokud se build nenalezne, tak se vyvolá jednotné chybové hlášení.
                            // Chybě se sníží priorita, není pro logování důležitá.
                            if (!buildArray.length) {
                              const error = new Error(
                                'No matching build found.'
                              );
                              throw errorWithLevelWarning(error);
                            }
                            // Vrátí se informace o prvním vyhovujícím buildu.
                            // TODO #44 Co kdyby bylo odfilrovaných buildů více? Throw?
                            const [build] = buildArray;
                            return Promise.resolve({
                              buildId: build.buildId,
                              url: build.url,
                              version: manifestData.version
                            });
                          })
                          .catch((error) => {
                            this.onError(error);
                            throw error;
                          })
                      );
                    }),
                  // Pouze pro inicializaci série, nedostane se mimo metodu.
                  // eslint-disable-next-line prefer-promise-reject-errors
                  Promise.reject()
                )
                // Pokud neuspěje žádný, vyvolá se jednotné chybové hlášení.
                // Chybě se sníží priorita, není pro logování důležitá.
                .catch(() => {
                  const error = new Error(
                    'No version manifest satisfied requirements.'
                  );
                  throw errorWithLevelWarning(error);
                })
            );
          })
      );
    });
  }
}

/**
 * Ověří dostupnost serveru.
 * @param {string} serverUrl URL adresa Ki-Wi Serveru pro komunikaci.
 * @returns {Promise<boolean>} Vrátí `true`, pokud server odpovídá, jinak `false`.
 * @param {number} [timeoutInMs=3000] Časový limit v milisekundách pro ping.
 */
export async function ping(serverUrl, timeoutInMs = 3000) {
  try {
    const response = await fetchWithTimeout(
      `${serverUrl}/api/ping`,
      {},
      timeoutInMs
    );
    return response.ok;
  } catch {
    // Pokud dojde k chybě, tak se nic nedělá.
  }
  return false;
}

/**
 * @typedef {Object} EventTypes
 * @property {ErrorEventListener} error
 * @property {HeartbeatFetchedEventListener} heartbeatFetched
 */

/**
 * Byla získána push data ze serveru.
 * @callback HeartbeatFetchedEventListener
 * @param {HeartbeatFetched} heartbeatFetched Heartbeat data.
 */
